import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { DayOfWeek, LocalDate } from "@js-joda/core";
import { type TimeReportDto } from "model/TimeReport";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  DaysList,
  DayRow,
  DayRowTop,
  DayName,
  Toggle,
  ToggleKnob,
  DayRowSelects,
  SelectGroup,
  SelectLabel,
  HourSelect,
  OvernightNote,
  ClosedNote,
} from "./analytics.operatingHours.styles";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AnalyticsPeriod = "day" | "week" | "month" | "year";

export interface DayOperatingHours {
  enabled: boolean;
  openHour: number; // 0–23
  closeHour: number; // 0–23
}

// Index 0 = Monday … 6 = Sunday
export type OperatingHours = DayOperatingHours[];

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_OPERATING_HOURS: OperatingHours = Array.from(
  { length: 7 },
  () => ({ enabled: true, openHour: 10, closeHour: 23 }),
);

// ─── localStorage helpers ─────────────────────────────────────────────────────

const STORAGE_KEY = "analytics_operating_hours_v2";

export const hasStoredOperatingHours = (): boolean =>
  localStorage.getItem(STORAGE_KEY) !== null;

export const loadOperatingHours = (): OperatingHours => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_OPERATING_HOURS;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === 7)
      return parsed.map(
        (d): DayOperatingHours => ({
          enabled: Boolean(d.enabled),
          openHour: Number(d.openHour),
          closeHour: Number(d.closeHour),
        }),
      );
  } catch {}
  return DEFAULT_OPERATING_HOURS;
};

export const saveOperatingHours = (hours: OperatingHours): void =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(hours));

// ─── Timestamp helpers ────────────────────────────────────────────────────────

/**
 * Parses an order timestamp into a local Date, treating the hour/minute in
 * the ISO string literally — ignoring any UTC/timezone offset.
 *
 * The POS system records wall-clock time (e.g. "20:31"), but the API may
 * append a Z suffix. Using `new Date("...20:31Z")` would shift the hour by
 * the browser's UTC offset (+2 h in Sweden), landing it in the 22:00 bucket.
 * Instead we extract the date/time components directly so "20:31" always
 * means the 20:xx bucket, regardless of timezone.
 */
export const parseTimestamp = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  const str = String(value).trim();
  // Match the numeric components of an ISO 8601 datetime string
  // e.g. "2024-07-15T20:31:00.000Z" or "2024-07-15T20:31:00+02:00"
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (m) {
    // Construct as local time — timezone offset in the string is intentionally ignored
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  }
  return new Date(str);
};

/**
 * Converts a genuine UTC timestamp (e.g. from time reports) into a Date whose
 * local-time methods — getHours(), getDay(), getDate() — return values in the
 * Swedish timezone (Europe/Stockholm), regardless of the browser's own timezone.
 *
 * The returned Date is NOT a "real" UTC instant; its internal millisecond value
 * is displaced so that arithmetic between two such Dates (e.g. endMs - startMs,
 * cursor < endMs) remains consistent as long as both operands are produced by
 * this function.
 *
 * Use this for time-report timestamps, which carry a real UTC offset.
 * For POS order timestamps (which carry a false Z suffix), use parseTimestamp.
 */
export const parseSwedishTime = (value: string | Date): Date => {
  const d = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const v = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };
  // % 24 guards against implementations that return 24 for midnight
  return new Date(
    v("year"),
    v("month") - 1,
    v("day"),
    v("hour") % 24,
    v("minute"),
    v("second"),
  );
};

// ─── Week bounds helper ───────────────────────────────────────────────────────

/** Returns the Monday and Sunday of the ISO week containing the given date. */
export const getWeekBounds = (
  date: LocalDate,
): { weekStart: LocalDate; weekEnd: LocalDate } => {
  const weekStart = date.with(DayOfWeek.MONDAY);
  return { weekStart, weekEnd: weekStart.plusDays(6) };
};

// ─── Time-report slot iterator ────────────────────────────────────────────────

/**
 * Iterates over each hour-aligned slot within a time report's working window.
 * The callback receives the slot start (as a Swedish local-time Date) and the
 * fractional hours the report actually covers within that slot.
 *
 * Centralises the cursor-walk logic shared by the chart bucket builders and
 * the business-hour gross calculator.
 */
export const walkTimeReportSlots = (
  report: TimeReportDto,
  callback: (slotStart: Date, fractionHours: number) => void,
): void => {
  if (!report.startTime || !report.endTime) return;
  const startMs = parseSwedishTime(report.startTime).getTime();
  const endMs = parseSwedishTime(report.endTime).getTime();
  if (endMs <= startMs) return;

  let slotStart = parseSwedishTime(report.startTime);
  slotStart.setMinutes(0, 0, 0);

  while (slotStart.getTime() < endMs) {
    const bucketStart = Math.max(slotStart.getTime(), startMs);
    const bucketEnd = Math.min(slotStart.getTime() + 3_600_000, endMs);
    callback(slotStart, (bucketEnd - bucketStart) / 3_600_000);
    slotStart = new Date(slotStart.getTime() + 3_600_000);
  }
};

// ─── Day-of-week index helper ─────────────────────────────────────────────────

// JS getDay() returns 0=Sun…6=Sat → convert to 0=Mon…6=Sun
export const jsDayToBucketIndex = (jsDay: number): number =>
  jsDay === 0 ? 6 : jsDay - 1;

// ─── Business-day utilities ───────────────────────────────────────────────────

/**
 * Returns true if `ts` falls within the operating window for `businessDate`.
 * Uses the operating hours for the weekday of businessDate.
 *
 * Overnight example (Mon open=15, close=3):
 *  - Mon 17:00 → true  (same date, after open)
 *  - Tue 02:40 → true  (next date, before close — Monday's overnight window)
 *  - Tue 10:00 → false (outside any window)
 */
export const isOrderInBusinessDay = (
  ts: Date,
  businessDate: LocalDate,
  operatingHours: OperatingHours,
): boolean => {
  // joda DayOfWeek.value(): 1=Mon…7=Sun → bucket index 0–6
  const bucketIdx = businessDate.dayOfWeek().value() - 1;
  const dayHours = operatingHours[bucketIdx];
  if (!dayHours.enabled) return false;

  const { openHour, closeHour } = dayHours;
  const hour = ts.getHours();
  const orderDate = LocalDate.of(
    ts.getFullYear(),
    ts.getMonth() + 1,
    ts.getDate(),
  );
  const isOvernight = closeHour < openHour;

  if (!isOvernight) {
    return (
      orderDate.isEqual(businessDate) && hour >= openHour && hour < closeHour
    );
  }
  return (
    (orderDate.isEqual(businessDate) && hour >= openHour) ||
    (orderDate.isEqual(businessDate.plusDays(1)) && hour < closeHour)
  );
};

/**
 * Returns true if the timestamp is within any valid operating window.
 * Used for week-mode filtering before bucket attribution.
 */
export const isOrderInBusinessWindow = (
  ts: Date,
  oh: OperatingHours,
): boolean => getBusinessDayOfWeek(ts, oh) !== null;

/**
 * Returns the JS day-of-week (0=Sun…6=Sat) of the business day this order
 * belongs to, or null if it falls in a dead zone.
 *
 * Overnight: an order at Tue 02:00 with Monday open=15/close=3 belongs to Monday.
 */
export const getBusinessDayOfWeek = (
  ts: Date,
  operatingHours: OperatingHours,
): number | null => {
  const hour = ts.getHours();
  const jsDay = ts.getDay();
  const bucketIdx = jsDayToBucketIndex(jsDay);
  const dayHours = operatingHours[bucketIdx];

  // Check if the timestamp belongs to this calendar day's window
  if (dayHours.enabled) {
    const { openHour, closeHour } = dayHours;
    const isOvernight = closeHour < openHour;
    if (!isOvernight) {
      if (hour >= openHour && hour < closeHour) return jsDay;
    } else {
      if (hour >= openHour) return jsDay;
    }
  }

  // Check if it's the post-midnight portion of the previous day's overnight window
  const prevJsDay = jsDay === 0 ? 6 : jsDay - 1;
  const prevBucketIdx = jsDayToBucketIndex(prevJsDay);
  const prevDayHours = operatingHours[prevBucketIdx];

  if (prevDayHours.enabled) {
    const { openHour: prevOpen, closeHour: prevClose } = prevDayHours;
    const isPrevOvernight = prevClose < prevOpen;
    if (isPrevOvernight && hour < prevClose) {
      return prevJsDay;
    }
  }

  return null; // dead zone
};

/**
 * Returns the calendar date (as a LocalDate) of the business day this timestamp
 * belongs to, adjusting for overnight windows.
 *
 * Example: if Monday operates 15:00→03:00, an order at 01:30 on Tuesday belongs
 * to Monday's business day and this function returns Monday's date.
 *
 * Returns null if the timestamp falls in a dead zone (no operating window covers it).
 */
export const getBusinessDate = (
  ts: Date,
  operatingHours: OperatingHours,
): LocalDate | null => {
  const businessJsDay = getBusinessDayOfWeek(ts, operatingHours);
  if (businessJsDay === null) return null;

  if (businessJsDay === ts.getDay()) {
    // Timestamp is within the current calendar day's window
    return LocalDate.of(ts.getFullYear(), ts.getMonth() + 1, ts.getDate());
  }

  // Overnight tail — business date is the previous calendar day
  const prev = new Date(ts.getTime() - 86_400_000);
  return LocalDate.of(prev.getFullYear(), prev.getMonth() + 1, prev.getDate());
};

/**
 * Returns the ordered list of hours in the operating window for a single day.
 * e.g. open=15, close=3 → [15, 16, …, 23, 0, 1, 2]
 * open === close is treated as 24-hour operation.
 */
export const getOperatingHoursList = (
  dayHours: DayOperatingHours,
): number[] => {
  if (!dayHours.enabled) return [];
  const { openHour, closeHour } = dayHours;
  if (openHour === closeHour) return Array.from({ length: 24 }, (_, i) => i);
  const hours: number[] = [];
  let h = openHour;
  while (h !== closeHour) {
    hours.push(h);
    h = (h + 1) % 24;
  }
  return hours;
};

// ─── Side pane component ──────────────────────────────────────────────────────

interface OperatingHoursPaneProps {
  initial: OperatingHours;
  open: boolean;
  onSave: (hours: OperatingHours) => void;
  onClose: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const DAY_KEYS: string[] = [
  "WeekDays.MONDAY",
  "WeekDays.TUESDAY",
  "WeekDays.WEDNESDAY",
  "WeekDays.THURSDAY",
  "WeekDays.FRIDAY",
  "WeekDays.SATURDAY",
  "WeekDays.SUNDAY",
];

export const OperatingHoursPane = (props: OperatingHoursPaneProps) => {
  const { t } = useTranslation();
  const [hours, setHours] = useState<OperatingHours>(props.initial);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) setHours(props.initial);
    wasOpen.current = props.open;
  }, [props.open, props.initial]);

  const updateDay = (index: number, patch: Partial<DayOperatingHours>) => {
    setHours((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const handleSave = () => {
    saveOperatingHours(hours);
    props.onSave(hours);
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.OperatingHours")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.OperatingHoursDescription")}
        </PaneDescription>
        <DaysList>
          {hours.map((day, i) => {
            const isOvernight = day.enabled && day.closeHour < day.openHour;
            return (
              <DayRow key={i}>
                <DayRowTop>
                  <DayName>{t(DAY_KEYS[i])}</DayName>
                  <Toggle
                    $enabled={day.enabled}
                    onClick={() => updateDay(i, { enabled: !day.enabled })}
                    role="switch"
                    aria-checked={day.enabled}
                  >
                    <ToggleKnob $enabled={day.enabled} />
                  </Toggle>
                </DayRowTop>
                {day.enabled ? (
                  <DayRowSelects>
                    <SelectGroup>
                      <SelectLabel>
                        {t("CompanyAnalytics.OpeningTime")}
                      </SelectLabel>
                      <HourSelect
                        value={day.openHour}
                        onChange={(e) =>
                          updateDay(i, { openHour: Number(e.target.value) })
                        }
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {String(h).padStart(2, "0")}:00
                          </option>
                        ))}
                      </HourSelect>
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>
                        {t("CompanyAnalytics.ClosingTime")}
                      </SelectLabel>
                      <HourSelect
                        value={day.closeHour}
                        onChange={(e) =>
                          updateDay(i, { closeHour: Number(e.target.value) })
                        }
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {String(h).padStart(2, "0")}:00
                          </option>
                        ))}
                      </HourSelect>
                    </SelectGroup>
                    {isOvernight && (
                      <OvernightNote>
                        {t("CompanyAnalytics.OvernightInfo")}
                      </OvernightNote>
                    )}
                  </DayRowSelects>
                ) : (
                  <ClosedNote>{t("CompanyAnalytics.Closed")}</ClosedNote>
                )}
              </DayRow>
            );
          })}
        </DaysList>
        <PaneFooter>
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.Save")}
            onClick={handleSave}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
